import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, ArrowDownUp, ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, ListFilter, Loader2, MoreHorizontal, Plus, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { BottomSheet } from "@/components/shared/PagePrimitives";
import { Segment } from "@/components/shared/FilterBar";
import { Kbd, isTypingTarget } from "@/components/shared/ListNav";
import { useSlidingPill, autoPill } from "@/components/shared/Pill";

// 345: jednotná lišta seznamů (audit lišty 28. 10. 2026, vzor nahled/vzor-lista.html).
// PC:      titulek a souhrn … Hledat · [ikona] · ⋯ · hlavní tlačítko
//          rychlé záložky · Filtr · Řadit … pohledy úplně vpravo
// Telefon: titulek a souhrn … ⋯ · „+"; pohledy jako podtržené záložky; pilulka rychlých záložek · Filtr · Řadit · Hledat
// Klávesy na PC: / hledat, F filtr, R řadit, N nová položka (ne při psaní ani nad otevřeným oknem).
export const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const overlayOpen = () => !!document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]');

// 383: zkratka viditelně „zmáčkne“ své tlačítko a štítek klávesy blikne — zkratky se tak učí samy.
function flashKey(key) {
  document.querySelectorAll(`kbd[data-kbd="${key}"]`).forEach((k) => {
    k.classList.remove("pp-kbd-hit"); void k.offsetWidth; k.classList.add("pp-kbd-hit");
    const b = k.closest("button");
    if (b) { b.classList.add("pp-pressed"); setTimeout(() => b.classList.remove("pp-pressed"), 140); }
  });
}

function useKeys(map, enabled) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return undefined;
    const h = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || isTypingTarget(e.target) || overlayOpen()) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const fn = ref.current[key];
      if (!fn) return;
      e.preventDefault();
      flashKey(key);
      fn();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [enabled]);
}

const KBD_ON_BRAND = "!bg-white/20 !border-white/30 !text-white";
const ROUND = "pp-press h-11 w-11 shrink-0 rounded-full border border-border bg-card pp-tint grid place-items-center text-foreground hover:text-brand transition-colors relative";

function SearchField({ value, onChange, placeholder, inputRef, className = "", autoFocus, onClose, kbd }) {
  return (
    <label className={`h-11 bg-card pp-tint border border-border rounded-full pl-4 pr-1.5 inline-flex items-center gap-2 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 ${className}`}>
      <Search size={16} className="text-muted-foreground shrink-0" aria-hidden="true" />
      <input ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} aria-label="Hledat"
        onKeyDown={(e) => { if (e.key === "Escape") e.currentTarget.blur(); }} data-testid="list-search"
        className="bg-transparent focus:outline-none w-full min-w-0 text-base md:text-sm" />
      {kbd && !value && <Kbd className="mr-1.5" data-kbd="/">/</Kbd>}
      {(value || onClose) && (
        <button type="button" onClick={() => { onChange(""); onClose?.(); }} aria-label={onClose ? "Zavřít hledání" : "Vymazat hledání"} data-testid="list-search-clear"
          className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground shrink-0"><X size={16} /></button>
      )}
    </label>
  );
}

// Položka menu: { icon, label, hint?, onClick, testid, sub? }; hint = šedý druhý řádek;
// sub = [položky] — vypíšou se pod nadpisem položky (např. Tisk deníku › zakázky).
function MenuItem({ it }) {
  return (
    <DropdownMenuItem onClick={it.onClick} data-testid={it.testid} className="py-2.5">
      {it.icon && <it.icon size={16} className="mr-2 shrink-0 text-brand" />}
      {it.hint ? <div><div className="font-semibold">{it.label}</div><div className="text-xs text-muted-foreground">{it.hint}</div></div> : it.label}
    </DropdownMenuItem>
  );
}
export function MenuItems({ items }) {   // 403: i pro rozbalovací tlačítko v Ekonomice (Podklad pro mzdy)
  return items.map((it) => (it.sub ? (
    <div key={it.label} data-testid={it.testid}>
      <DropdownMenuLabel className="flex items-center gap-2 px-2 pt-2 pb-1 text-xs font-semibold text-muted-foreground">
        {it.icon && <it.icon size={14} />}{it.label}
      </DropdownMenuLabel>
      {it.sub.map((x) => <MenuItem key={x.label} it={x} />)}
    </div>
  ) : <MenuItem key={it.label} it={it} />));
}

function MoreMenu({ items, busy }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={ROUND} aria-label="Další akce" title="Další akce" data-testid="list-more-btn" disabled={busy} aria-busy={busy || undefined}>
          {busy ? <Loader2 size={18} className="animate-spin motion-reduce:animate-none" /> : <MoreHorizontal size={18} />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[230px] max-h-[70vh] overflow-y-auto"><MenuItems items={items} /></DropdownMenuContent>
    </DropdownMenu>
  );
}

// Akce hlavičky (vpravo od titulku): Hledat · ikona · ⋯ · hlavní tlačítko.
// `primary` = { label: "Zakázka", full: "Nová zakázka", onClick, testid, menu?, menuFooter?, icon? (výchozí +) }
// (menu = položky jako u ⋯ — tlačítko pak otevře výběr, N také); `iconAction` = { icon, label, onClick, testid }
// (na telefonu se přesune do ⋯); `more` = [{ icon, label, hint?, onClick, testid, sub? }]; `moreBusy` = ⋯ se točí (export).
export function ListActions({ search, onSearch, searchPh = "Hledat…", primary, iconAction, more = [], moreBusy, mobile, className = "" }) {
  const searchRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const runPrimary = () => (primary?.menu ? setMenuOpen(true) : primary?.onClick());
  useKeys({ "/": onSearch ? () => searchRef.current?.focus() : undefined, n: primary && !primary.disabled ? runPrimary : undefined }, !mobile);
  const moreItems = mobile && iconAction ? [iconAction, ...more] : more;
  const PIcon = primary?.icon || Plus;
  const primaryBtn = primary && (mobile ? (
    <button type="button" onClick={primary.menu ? undefined : primary.onClick} aria-label={primary.full} data-testid={primary.testid} disabled={primary.disabled}
      className="pp-press h-11 w-11 shrink-0 rounded-full bg-brand text-white grid place-items-center shadow-sm disabled:opacity-60"><PIcon size={20} /></button>
  ) : (
    // 359: oblé jako hledání, Filtr a záložky vedle (dřív výchozí rounded-md tlačítka)
    <Button onClick={primary.menu ? undefined : primary.onClick} data-testid={primary.testid} disabled={primary.disabled} className="h-11 px-5 rounded-full font-bold shrink-0" title={`${primary.full} (N)`}>
      <PIcon size={18} /> {primary.label} {primary.menu && <ChevronDown size={15} />}<Kbd className={`ml-1 ${KBD_ON_BRAND}`} data-kbd="n">N</Kbd>
    </Button>
  ));
  return (
    <div className={`flex items-center gap-2 ml-auto max-w-full ${className}`}>
      {!mobile && onSearch && <SearchField value={search} onChange={onSearch} placeholder={searchPh} inputRef={searchRef} kbd className="w-[240px] xl:w-[300px] min-w-0" />}
      {!mobile && iconAction && (
        <button type="button" onClick={iconAction.onClick} className={ROUND} aria-label={iconAction.label} title={iconAction.label} data-testid={iconAction.testid}>
          <iconAction.icon size={18} />
        </button>
      )}
      {moreItems.length > 0 && <MoreMenu items={moreItems} busy={moreBusy} />}
      {primary?.menu ? (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>{primaryBtn}</DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[270px] max-h-[70vh] overflow-y-auto"><MenuItems items={primary.menu} />{primary.menuFooter}</DropdownMenuContent>
        </DropdownMenu>
      ) : primaryBtn}
    </div>
  );
}

// Hlavička seznamu: titulek a souhrn vlevo, ListActions vpravo.
export function ListHeader({ title, summary, mobile, testid = "list-header", ...actions }) {
  return (
    <div className="flex flex-wrap items-start gap-x-2 gap-y-3" data-testid={testid}>
      <div className="min-w-[180px] flex-1">
        <h1 className="text-3xl font-black tracking-tighter uppercase">{title}</h1>
        {summary && <p className="text-[13.5px] text-muted-foreground mt-1" data-testid={`${testid}-summary`}>{summary}</p>}
      </div>
      <ListActions mobile={mobile} {...actions} />
    </div>
  );
}

// 352: modul s vlastní hlavičkou a podzáložkami (Ekonomika) — záložka pošle své akce do hlavičky modulu.
// Bez místa v hlavičce (např. Doklady vložené v zakázce) se akce vykreslí na místě.
export const HeaderSlot = createContext(null);
export function SlotActions(props) {
  const el = useContext(HeaderSlot);
  return el ? createPortal(<ListActions {...props} />, el) : <ListActions {...props} />;
}

/* ---------- Filtr (pole → hodnoty, jako Linear) ---------- */
function FilterMenu({ filter, start, onDone, mobile }) {
  const [step, setStep] = useState(start || null);
  const [q, setQ] = useState("");
  const [act, setAct] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { setQ(""); setAct(0); if (!mobile) inputRef.current?.focus(); }, [step, mobile]);
  const g = step ? filter.groupOf(step) : null;
  const nq = norm(q);
  let items = [];
  if (!g) {
    items = filter.groups.filter((x) => !nq || norm(x.label).includes(nq)).map((x) => ({ kind: "field", g: x }));
    if (nq) filter.groups.forEach((x) => (x.options || []).forEach((o) => { if (norm(o.label).includes(nq)) items.push({ kind: "val", g: x, o }); }));
    items = items.slice(0, 40);
  } else if (!g.type) {
    items = (g.options || []).filter((o) => !nq || norm(o.label).includes(nq)).map((o) => ({ kind: "check", g, o }));
  }
  const pick = (it, how) => {
    if (it.kind === "field" && it.g.external && !it.g.render) { onDone(); it.g.external.open(); return; }   // pole s vlastním oknem
    if (it.kind === "field") { setStep(it.g.key); return; }
    filter.toggleValue(it.g.key, it.o.value);
    if (it.kind === "val" || how === "enter") onDone();
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAct((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAct((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (items[act]) pick(items[act], e.shiftKey ? "keep" : "enter"); }
    else if (e.key === "Backspace" && !q && step) { e.preventDefault(); setStep(null); }
  };
  const row = mobile ? "min-h-[52px] text-[15.5px]" : "min-h-[38px] text-sm";
  const raw = g?.type ? filter.state[g.key]?.vals[0] || "" : "";
  const [from, to] = raw.split("..");
  let lastGroup = null;
  // 359: jediné Zpět je ‹ v hlavičce (a Backspace v prázdném hledání) — dole už „‹ Jiné pole“ není
  const head = (!mobile || g) && (
    <div className="flex items-center gap-2 px-3 pt-3 pb-2">
      {g && <button type="button" onClick={() => setStep(null)} aria-label="Zpět na pole" title="Zpět (Backspace)" data-testid="list-filter-back" className="w-8 h-8 rounded-full bg-secondary grid place-items-center"><ChevronLeft size={16} /></button>}
      <span className="font-bold text-[15px] flex-1">{g ? g.label : "Filtr"}</span>
    </div>
  );
  // pole s vlastním obsahem kroku (Úkoly › Kdo: rychlé volby, uložené výběry, lidé po rolích) — ve stejném okně
  if (g?.render) return <div className="flex flex-col min-h-0" data-testid="list-filter-menu">{head}{g.render({ mobile, onDone, onBack: () => setStep(null) })}</div>;
  return (
    <div className="flex flex-col min-h-0" data-testid="list-filter-menu">
      {head}
      {!g?.type && (!mobile || !g || (g.options || []).length > 8) && (
        <div className="mx-3 mb-1.5 h-10 rounded-[12px] bg-secondary flex items-center gap-2 px-3">
          <Search size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setAct(0); }} onKeyDown={onKey} data-testid="list-filter-input"
            placeholder={g ? `${g.label}…` : "Filtrovat podle…"} aria-label={g ? g.label : "Filtrovat podle"}
            className="bg-transparent focus:outline-none w-full min-w-0 text-base md:text-sm" />
        </div>
      )}
      {g?.type === "date" && (
        <div className="px-3 pb-3"><input type="date" value={raw} onChange={(e) => filter.setRaw(g.key, e.target.value)} data-testid="list-filter-date"
          className="h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-base md:text-sm" /></div>
      )}
      {g?.type === "range" && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-2">
          <label className="text-xs text-muted-foreground font-semibold min-w-0">Od<input type="date" value={from || ""} onChange={(e) => filter.setRaw(g.key, e.target.value || to ? `${e.target.value}..${to || ""}` : "")} data-testid="list-filter-from"
            className="mt-1 h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-base md:text-sm text-foreground" /></label>
          <label className="text-xs text-muted-foreground font-semibold min-w-0">Do<input type="date" value={to || ""} onChange={(e) => filter.setRaw(g.key, from || e.target.value ? `${from || ""}..${e.target.value}` : "")} data-testid="list-filter-to"
            className="mt-1 h-11 w-full min-w-0 rounded-[12px] border border-border bg-card px-3 text-base md:text-sm text-foreground" /></label>
        </div>
      )}
      {!g?.type && (
        <ul className={`overflow-y-auto px-1.5 pb-2 ${mobile ? "" : "max-h-[340px]"}`} role="listbox" aria-label={g ? g.label : "Pole filtru"}>
          {items.map((it, i) => {
            const head = it.kind === "val" && lastGroup !== "val" ? <li key="hodnoty" className="px-2.5 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Hodnoty</li> : null;
            lastGroup = it.kind;
            const on = it.kind === "check" && filter.isOn(it.g.key, it.o.value);
            const count = it.o ? filter.counts[it.g.key]?.[it.o.value] : null;
            return [head, (
              <li key={`${it.kind}-${it.g.key}-${it.o?.value || ""}`}>
                <button type="button" role="option" aria-selected={i === act} onMouseEnter={() => setAct(i)} onClick={() => pick(it, "click")}
                  data-testid={it.kind === "field" ? `list-filter-field-${it.g.key}` : `list-filter-value-${it.g.key}-${it.o.value}`}
                  className={`w-full flex items-center gap-2.5 px-2.5 rounded-[10px] text-left ${row} ${i === act ? "bg-secondary" : ""}`}>
                  {it.kind === "check"
                    ? <span className={`w-[18px] h-[18px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 ${on ? "bg-brand border-brand text-white" : "border-muted-foreground/60"}`}>{on && <Check size={13} strokeWidth={3} />}</span>
                    : it.g.icon ? <it.g.icon size={16} className="text-muted-foreground shrink-0" /> : null}
                  <span className="flex-1 min-w-0 truncate">{it.kind === "field" ? it.g.label : it.o.label}</span>
                  {it.kind === "val" && <span className="text-xs text-muted-foreground shrink-0">{it.g.label}</span>}
                  {count != null && it.kind === "check" && <span className="text-xs text-muted-foreground tabular-nums shrink-0">{count}</span>}
                  {it.kind === "field" && <ChevronRight size={16} className="text-muted-foreground shrink-0" />}
                </button>
              </li>
            )];
          })}
          {!items.length && <li className="px-3 py-3 text-sm text-muted-foreground">Nic nenalezeno</li>}
        </ul>
      )}
      {g && (
        <div className="flex items-center gap-2 border-t border-border px-3 pt-2.5 pb-3">
          <span className="flex-1" />
          {filter.state[g.key] && <button type="button" onClick={() => { filter.clearOne(g.key); onDone(); }} className="text-[13px] font-semibold text-muted-foreground hover:text-destructive">Zrušit</button>}
          <Button onClick={onDone} className="h-9 font-bold" data-testid="list-filter-done">Hotovo</Button>
        </div>
      )}
    </div>
  );
}

function SmallSeg({ options, value, onChange, testid }) {
  return (
    <div ref={autoPill} className="flex p-1 bg-secondary rounded-full gap-0.5" role="radiogroup" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={`flex-1 h-9 rounded-full text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 whitespace-nowrap px-2 ${value === o.value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
          {o.icon && <o.icon size={14} />}{o.label}
        </button>
      ))}
    </div>
  );
}

function Chips({ options, value, onChange, testid, mobile }) {
  return (
    <div className="flex flex-wrap gap-1.5 px-0.5" role="radiogroup" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
          className={`${mobile ? "h-10 px-4 text-[15px]" : "h-8 px-3 text-[13px]"} rounded-full border font-semibold whitespace-nowrap transition-colors motion-reduce:transition-none ${value === o.value ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// sort = { options:[{value,label}], value, onChange, dir, onDir, dirLabels:[vzestupně, sestupně], sections:[{label, options, value, onChange, testid}] }
// 373: sekce s `chips: true` = výběr z víc možností jako čipy (Seskupit v Dokladech); `badge` = text za „Řadit:“ na tlačítku.
function SortMenu({ sort, mobile }) {
  const lab = "text-xs font-semibold text-muted-foreground px-1 pt-2 pb-1.5";
  return (
    <div className={mobile ? "pb-2" : "p-3"} data-testid="list-sort-menu">
      {!mobile && <div className="font-bold text-[15px] pb-1">Řadit</div>}
      {(sort.sections || []).map((s) => (
        <div key={s.label}><div className={lab}>{s.label}</div>{s.chips ? <Chips options={s.options} value={s.value} onChange={s.onChange} testid={s.testid} mobile={mobile} />
          : <SmallSeg options={s.options} value={s.value} onChange={s.onChange} testid={s.testid} />}</div>
      ))}
      {sort.options?.length > 0 && (
        <>
          {sort.onDir && (<><div className={lab}>Směr</div>
            <SmallSeg options={[{ value: "asc", label: sort.dirLabels?.[0] || "Vzestupně", icon: ArrowUp }, { value: "desc", label: sort.dirLabels?.[1] || "Sestupně", icon: ArrowDown }]}
              value={sort.dir} onChange={sort.onDir} testid="list-sort-dir" /></>)}
          <div className={lab}>Podle</div>
          <ul role="radiogroup" aria-label="Řadit podle">
            {sort.options.map((o) => (
              <li key={o.value}>
                <button type="button" role="radio" aria-checked={sort.value === o.value} onClick={() => sort.onChange(o.value)} data-testid={`list-sort-${o.value}`}
                  className={`w-full flex items-center gap-2.5 px-2 rounded-[10px] text-left hover:bg-secondary ${mobile ? "min-h-[50px] text-[15.5px]" : "min-h-[38px] text-sm"}`}>
                  <span className={`w-[18px] h-[18px] rounded-full border-[1.5px] grid place-items-center shrink-0 ${sort.value === o.value ? "border-brand bg-brand" : "border-muted-foreground/60"}`}>
                    {sort.value === o.value && <span className="w-[7px] h-[7px] rounded-full bg-white" />}
                  </span>
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function FilterPills({ filter, mobile, onOpen: openKey }) {
  if (!filter?.filters.length) return null;
  const onOpen = (key) => { const g = filter.groupOf(key); if (g?.external && !g.render) g.external.open(); else openKey(key); };
  const text = (f) => (f.labels.length > 2 ? `${f.labels.length} ${f.labels.length < 5 ? "hodnoty" : "hodnot"}` : f.labels.join(", "));
  const part = "inline-flex items-center gap-1.5 px-3 border-l border-brand/25 first:border-l-0";
  return (
    <div className={`flex items-center gap-2 ${mobile ? "overflow-x-auto no-scrollbar -mx-1 px-1" : "flex-wrap"}`} data-testid="list-filter-pills">
      {filter.filters.map((f) => (
        <span key={f.key} role="group" aria-label={`Filtr ${f.label}`} data-testid={`list-pill-${f.key}`}
          className="inline-flex items-stretch h-9 shrink-0 rounded-full border border-brand/40 bg-brand/[0.08] overflow-hidden text-[13px]">
          {mobile ? (
            <button type="button" onClick={() => onOpen(f.key)} className={part}>
              <span className="font-semibold pp-ink">{f.label}{f.neg ? " není" : ""}:</span><span className="font-semibold max-w-[180px] truncate">{text(f)}</span>
            </button>
          ) : (
            <>
              <button type="button" onClick={() => onOpen(f.key)} className={`${part} font-semibold pp-ink`}>{f.icon && <f.icon size={14} />}{f.label}</button>
              {!f.type && !f.noNeg && <button type="button" onClick={() => filter.toggleNeg(f.key)} title="Přepnout je / není" data-testid={`list-pill-neg-${f.key}`} className={`${part} text-muted-foreground`}>{f.neg ? "není" : "je"}</button>}
              <button type="button" onClick={() => onOpen(f.key)} className={`${part} font-semibold max-w-[260px]`}><span className="truncate">{text(f)}</span></button>
            </>
          )}
          <button type="button" onClick={() => filter.clearOne(f.key)} aria-label={`Odebrat filtr ${f.label}`} data-testid={`list-pill-x-${f.key}`}
            className="px-2.5 border-l border-brand/25 text-muted-foreground hover:text-destructive grid place-items-center"><X size={14} /></button>
        </span>
      ))}
      {!mobile && <button type="button" onClick={filter.clear} className="text-[13px] font-semibold text-brand underline" data-testid="list-filter-clear">Zrušit filtry</button>}
    </div>
  );
}

// Pohledy vpravo (ikony) — 383: vybraný má posuvný podklad s pružinou.
function ViewSwitch({ views, view, onView, testid }) {
  const sp = useSlidingPill(view, views.map((v) => `${v.value}${v.count ?? ""}`).join("|"));
  return (
    <div ref={sp.ref} className="relative flex h-11 p-1 bg-card pp-tint border border-border rounded-full" role="radiogroup" aria-label="Pohled" data-testid={`${testid}-views`}>
      {sp.pill("bg-brand")}
      {views.map((v) => (
        <button key={v.value} type="button" role="radio" aria-checked={view === v.value} aria-label={v.count ? `${v.label} (${v.count})` : v.label} title={v.count ? `${v.label} · ${v.count}` : v.label} onClick={() => onView(v.value)} data-testid={v.testid || `list-view-${v.value}`}
          data-pill-on={view === v.value || undefined}
          className={`relative z-[1] h-full w-10 rounded-full grid place-items-center transition-colors ${view === v.value ? `${sp.ready ? "" : "bg-brand"} text-white` : "text-muted-foreground hover:text-foreground"}`}>
          <v.icon size={17} />
          {v.count > 0 && <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] rounded-full bg-brand text-white text-[10.5px] font-bold grid place-items-center px-1 border-2 border-background">{v.count}</span>}
        </button>
      ))}
    </div>
  );
}

// tabs = [{ value, label, short?, count? }] — rychlé záložky (short = popisek v pilulce na telefonu); views = [{ value, label, short?, icon, count?, testid? }];
// filter = useListFilters(); sort viz SortMenu (sort.active = tlačítko svítí, když není výchozí).
// 377: sort.render({ mobile, close }) = vlastní obsah bubliny (Seskupit a řadit); sort.icon / label / title / width;
// 378: sort.hotkey (Zobrazení = V), sort.hint = co je nastavené (jen v bublině po najetí, na tlačítku se nezobrazuje).
// tabLabel = popis, když žádná záložka neplatí (např. vlastní období z filtru).
export function ListToolbar({ mobile, tabs, tab, tabLabel, onTab, filter, sort, views, view, onView, search = "", onSearch, searchPh = "Hledat…", testid = "list-toolbar" }) {
  const [fOpen, setFOpen] = useState(false);
  const [fStart, setFStart] = useState(null);
  const [sOpen, setSOpen] = useState(false);
  const [tOpen, setTOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openFilter = (key = null) => { setFStart(key); setFOpen(true); };
  const sortKey = sort?.hotkey || "r";
  useKeys({ f: filter ? () => openFilter() : undefined, [sortKey]: sort ? () => setSOpen(true) : undefined }, !mobile);
  const nf = filter?.filterCount || 0;
  const cur = tabs ? tabs.find((t) => t.value === tab) || { value: tab, label: tabLabel || "Vlastní" } : null;
  const showViews = views && views.length > 1;
  const viewUl = useSlidingPill(view, (views || []).map((v) => `${v.value}${v.count ?? ""}`).join("|"));   // 385: podtržení pohledu na telefonu

  if (!mobile) {
    const btn = "pp-press h-11 px-4 rounded-full border bg-card pp-tint inline-flex items-center gap-2 font-bold text-[14.5px] relative transition-colors";
    return (
      <div className="space-y-3" data-testid={testid}>
        <div className="flex items-center gap-2 flex-wrap">
          {tabs && <Segment tabs={tabs.map((t) => ({ value: t.value, label: t.label, count: t.count, testid: `list-tab-${t.value}` }))} value={tab} onChange={onTab} testid={`${testid}-tabs`} compact />}
          {filter && (
            <Popover open={fOpen} onOpenChange={setFOpen}>
              <PopoverTrigger asChild>
                <button type="button" onClick={(e) => { e.preventDefault(); if (fOpen) setFOpen(false); else openFilter(); }} data-testid="list-filter-btn" title="Filtr (F)"
                  className={`${btn} ${nf ? "border-brand text-brand" : "border-border text-foreground hover:text-brand"}`}>
                  <ListFilter size={17} /> Filtr <Kbd data-kbd="f">F</Kbd>
                  {nf > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[19px] h-[19px] rounded-full bg-brand text-white text-[11px] font-bold grid place-items-center px-1 border-2 border-background" data-testid="list-filter-count">{nf}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[340px] p-0 rounded-[16px]" onOpenAutoFocus={(e) => e.preventDefault()}>
                {fOpen && <FilterMenu key={fStart || "-"} filter={filter} start={fStart} onDone={() => setFOpen(false)} />}
              </PopoverContent>
            </Popover>
          )}
          {sort && (
            <Popover open={sOpen} onOpenChange={setSOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="list-sort-btn" title={sort.title ? `${sort.title} (${sortKey.toUpperCase()})${sort.hint ? `: ${sort.hint}` : ""}` : `Řadit (R)${sort.options ? `: ${sort.options.find((o) => o.value === sort.value)?.label || ""}` : ""}`}
                  className={`${btn} ${sort.active ? "border-brand text-brand" : "border-border text-foreground hover:text-brand"}`}>
                  {(() => { const Icon = sort.icon || ArrowDownUp; return <Icon size={17} />; })()} {sort.label ?? (sort.badge ? `Řadit: ${sort.badge}` : "Řadit")} <Kbd data-kbd={sortKey}>{sortKey.toUpperCase()}</Kbd></button>
              </PopoverTrigger>
              <PopoverContent align="start" collisionPadding={12} className="p-0 rounded-[16px]" style={{ width: sort.width || 320, maxWidth: "calc(100vw - 24px)" }}
                onOpenAutoFocus={sort.render ? (e) => e.preventDefault() : undefined}>
                {sort.render ? sort.render({ mobile: false, close: () => setSOpen(false) }) : <SortMenu sort={sort} />}
              </PopoverContent>
            </Popover>
          )}
          <span className="flex-1" />
          {showViews && (
            <ViewSwitch views={views} view={view} onView={onView} testid={testid} />
          )}
        </div>
        <FilterPills filter={filter} onOpen={openFilter} />
      </div>
    );
  }

  const round = "pp-press h-12 w-12 shrink-0 rounded-full border bg-card pp-tint grid place-items-center relative transition-colors";
  return (
    <div className="space-y-3" data-testid={testid}>
      {showViews && (
        <div className="border-b border-border">
          <nav ref={viewUl.ref} className="relative flex" aria-label="Pohled" data-testid={`${testid}-views`}>
            {viewUl.pill("bg-brand", { underline: true })}
            {views.map((v) => (
              <button key={v.value} type="button" onClick={() => onView(v.value)} aria-pressed={view === v.value} data-testid={v.testid || `list-view-${v.value}`} data-pill-on={view === v.value || undefined}
                className={`flex-1 flex items-center justify-center gap-2 px-2 min-h-[46px] text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${view === v.value ? `${viewUl.ready ? "border-transparent" : "border-brand"} text-brand` : "border-transparent text-muted-foreground"}`}>
                <v.icon size={17} /> {v.short || v.label}
                {v.count > 0 && <span className="rounded-full bg-brand text-white px-1.5 text-[11px] font-bold tabular-nums">{v.count}</span>}
              </button>
            ))}
          </nav>
        </div>
      )}
      {(searchOpen || search) && cur ? (
        <SearchField value={search} onChange={onSearch} placeholder={searchPh} autoFocus={searchOpen} onClose={() => setSearchOpen(false)} className="w-full h-12" />
      ) : (cur || filter || sort || onSearch) && (
        <div className="flex items-center gap-2">
          {!cur && onSearch ? (
            <SearchField value={search} onChange={onSearch} placeholder={searchPh} className="flex-1 min-w-0 h-12" />
          ) : cur ? (
            <button type="button" onClick={() => setTOpen(true)} data-testid="list-tabs-pill" aria-label={`Rychlé záložky: ${cur.label}`}
              className="h-12 flex-1 min-w-0 rounded-full border border-border bg-card pp-tint pl-4 pr-3 flex items-center gap-2 font-bold text-[15px]">
              <span className="flex-1 min-w-0 truncate text-left">{cur.short || cur.label}</span>
              {cur.count != null && <span className="rounded-full bg-brand/10 pp-ink px-2 py-px text-[12px] font-bold tabular-nums">{cur.count}</span>}
              <ChevronDown size={17} className="text-muted-foreground shrink-0" />
            </button>
          ) : <span className="flex-1" />}
          {filter && (
            <button type="button" onClick={() => openFilter()} aria-label="Filtr" data-testid="list-filter-btn" className={`${round} ${nf ? "border-brand text-brand" : "border-border"}`}>
              <ListFilter size={19} />
              {nf > 0 && <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] rounded-full bg-brand text-white text-[11px] font-bold grid place-items-center px-1 border-2 border-background" data-testid="list-filter-count">{nf}</span>}
            </button>
          )}
          {sort && <button type="button" onClick={() => setSOpen(true)} aria-label={sort.title || "Řadit"} data-testid="list-sort-btn" className={`${round} ${sort.active ? "border-brand text-brand" : "border-border"}`}>
            {(() => { const Icon = sort.icon || ArrowDownUp; return <Icon size={19} />; })()}</button>}
          {onSearch && cur && <button type="button" onClick={() => setSearchOpen(true)} aria-label="Hledat" data-testid="list-search-open" className={`${round} border-border`}><Search size={19} /></button>}
        </div>
      )}
      <FilterPills filter={filter} mobile onOpen={openFilter} />
      {filter && (
        <BottomSheet open={fOpen} onOpenChange={setFOpen} title="Filtr" testid="list-filter-sheet">
          {fOpen && <FilterMenu key={fStart || "-"} filter={filter} start={fStart} onDone={() => setFOpen(false)} mobile />}
        </BottomSheet>
      )}
      {sort && <BottomSheet open={sOpen} onOpenChange={setSOpen} title={sort.title || "Řadit"} testid="list-sort-sheet">
        {sort.render ? (sOpen && sort.render({ mobile: true, close: () => setSOpen(false) })) : <SortMenu sort={sort} mobile />}</BottomSheet>}
      {tabs && (
        <BottomSheet open={tOpen} onOpenChange={setTOpen} title="Rychlé záložky" testid="list-tabs-sheet">
          <ul role="radiogroup" aria-label="Rychlé záložky">
            {tabs.map((t) => (
              <li key={t.value}>
                <button type="button" role="radio" aria-checked={t.value === tab} onClick={() => { onTab(t.value); setTOpen(false); }} data-testid={`list-tab-${t.value}`}
                  className="w-full flex items-center gap-3 px-2 min-h-[52px] rounded-[12px] text-[15.5px] text-left hover:bg-secondary">
                  <span className={`w-5 h-5 rounded-full border-[1.5px] grid place-items-center shrink-0 ${t.value === tab ? "border-brand bg-brand" : "border-muted-foreground/60"}`}>
                    {t.value === tab && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  <span className="flex-1">{t.label}</span>
                  {t.count != null && <span className="text-sm text-muted-foreground tabular-nums">{t.count}</span>}
                </button>
              </li>
            ))}
          </ul>
        </BottomSheet>
      )}
    </div>
  );
}
